import {useEffect, useState} from 'react'
import {useParams, useNavigate, useLocation} from 'react-router-dom'
import {useAuth} from 'app/modules/auth'
import {
  doc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore'
import db from 'services/firebase'
import ReviewHeader from '../components/ReviewHeader'
import {showSuccessToast, showErrorToast} from 'app/components/ShowToast'
import {LocationState} from '../utils/PagesUtils'
import EmptyState from 'app/components/EmptyState'
import {KTCard, toAbsoluteUrl} from '_metronic/helpers'
import OwnerDisplay from 'app/components/OwnerDisplay'
import StatusBadge from 'app/components/StatusBadge'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import UploadEvidenceModal from '../components/Modals/UploadEvidenceModal'
import {ToastContainer} from 'react-toastify'

const AccessChange = () => {
  const [reviews, setReviews] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('access-changes')
  const [isLoading, setIsLoading] = useState(true)
  const {currentUser} = useAuth()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {toast} = (location.state as LocationState) || {}
  const [sortBy, setSortBy] = useState('system')
  const [sortDirection, setSortDirection] = useState('asc')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [systemId, setSystemId] = useState('')
  const [userId, setUserId] = useState('')
  const [reviewId, setReviewId] = useState('')
  const [remediatedNote, setRemediatedNote] = useState('')
  const [remediatedFile, setRemediatedFile] = useState(null)
  const [mode, setMode] = useState<'Add' | 'Edit'>('Add')

  useEffect(() => {
    if (toast) {
      const {message, type} = toast
      if (type === 'success') {
        showSuccessToast({message})
      } else if (type === 'error') {
        showErrorToast({message})
      }
      window.history.replaceState({}, document.title, location.pathname)
    }
  }, [toast])

  useEffect(() => {
    if (location.pathname.includes('access-changes')) {
      setActiveTab('access-changes')
    } else {
      setActiveTab('systems')
    }
  }, [location.pathname, id])

  useEffect(() => {
    const unsubscribe = getReviews()
    return () => unsubscribe && unsubscribe()
  }, [id, currentUser])

  const getReviews = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id) {
        setIsLoading(false)
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = doc(tenantRef, 'reviews', id)

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const policyData = snapshot.data()
        setReviews(policyData ? [{id: snapshot.id, ...policyData}] : [])
        setIsLoading(false)
      })

      return unsubscribe
    } catch (error) {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let filtered = reviews
      .map((review) => ({
        ...review,
        systems: Object.entries(review.systems || {}).map(([systemId, system]: [string, any]) => ({
          systemId,
          ...system,
        })),
      }))
      .flatMap((review) =>
        review.systems.map((system) => ({
          ...review,
          system,
          deniedUsers: Object.entries(system.users || {})
            .filter(([_, user]: [string, any]) => user.status === 'Denied')
            .map(([userId, user]: [string, any]) => ({
              userId,
              ...user,
            })),
        }))
      )
      .flatMap((item) =>
        item.deniedUsers.map((user) => ({
          ...item,
          user,
        }))
      )

    const sortedItems = filtered.sort((a, b) => {
      if (sortBy === 'system') {
        const valueA = a.system?.name || ''
        const valueB = b.system?.name || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'accountName') {
        const valueA = a.user?.name || ''
        const valueB = b.user?.name || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'reviewedBy') {
        const valueA = a.user?.reviewBy?.name || ''
        const valueB = b.user?.reviewBy?.name || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'decision' || sortBy === 'note' || sortBy === 'remediationStatus') {
        const valueA = a.user?.[sortBy] || ''
        const valueB = b.user?.[sortBy] || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      }
      return 0
    })

    setFilteredItems(sortedItems)
  }, [reviews, sortBy, sortDirection])

  const handleOpenModal = (
    dueDate: string,
    id: string,
    reviewer: {id: string; name: string; email: string}
  ) => {
    console.log('Opening modal for review:', {dueDate, id, reviewer})
  }

  const handleComplete = async () => {
    const tenantId = currentUser?.tenantId || ''
    const reviewRef = doc(db, `tenants/${tenantId}/reviews/${id}`)

    try {
      await updateDoc(reviewRef, {
        status: 'Completed',
        updatedAt: serverTimestamp(),
        dueDate: serverTimestamp(),
        completedAt: serverTimestamp(),
      })
      navigate('/access/review', {
        state: {toast: {message: 'Review completed successfully', type: 'success'}},
      })
    } catch (error) {
      showErrorToast({message: 'Failed to complete review. Please try again.'})
    }
  }

  const handleDelete = async (reviewId: string, systemId?: string, isReview?: boolean) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }

      if (isReview) {
        const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)
        await deleteDoc(reviewRef)
        navigate('/access/review')
        showSuccessToast({message: 'Review deleted successfully'})
      } else if (systemId) {
        const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)
        await updateDoc(reviewRef, {
          [`systems.${systemId}`]: deleteField(),
        })
        showSuccessToast({message: 'System removed successfully'})
      }
    } catch (error) {
      showErrorToast({message: 'Failed to delete. Please try again.'})
    }
  }

  const isCompleteButtonDisabled =
    reviews.length > 0 &&
    reviews[0].systems &&
    Object.values(reviews[0].systems).some((system: any) => system.status !== 'Completed')

  const handleSort = (column: string) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  }

  const handleUploadModal = (
    id: string,
    systemId: string,
    userId: string,
    remediatedNote = '',
    remediatedFile = null,
    mode: 'Add' | 'Edit' = 'Add'
  ) => {
    setShowUploadModal(true)
    setSystemId(systemId)
    setUserId(userId)
    setReviewId(id)
    setRemediatedNote(remediatedNote)
    setRemediatedFile(remediatedFile)
    setMode(mode)
  }

  return (
    <>
      {filteredItems.length > 0 && (
        <div
          className='card'
          style={{
            backgroundColor: '#F3FAF7',
            border: '1.5px solid #0E9F6E',
            marginBottom: '24px',
          }}
        >
          <div className='px-6 py-4'>
            <div className='d-flex align-items-between justify-content-between'>
              <div className='d-flex justify-content-start flex-column gap-1'>
                <span className='card-label fw-semibold fs-4'>
                  Adjust or revoke access permissions for individual accounts
                </span>
                <span className='fw-normal fs-6' style={{color: '#5A5A5A'}}>
                  Reviewers have flagged the accounts below for access removal or permission
                  changes.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <ReviewHeader
        reviews={reviews}
        activeTab={activeTab}
        filteredItems={filteredItems}
        isCompleteButtonDisabled={isCompleteButtonDisabled}
        handleOpenModal={handleOpenModal}
        handleComplete={handleComplete}
        handleDelete={handleDelete}
        type='access-change'
      />
      <KTCard>
        <div className='card-body py-3'>
          <div className='table-responsive' style={{minHeight: '50vh', overflowY: 'auto'}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold' style={{color: '#5A5A5A'}}>
                  <th className='min-w-200px' onClick={() => handleSort('system')}>
                    SYSTEM{' '}
                    {sortBy === 'system' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-150px' onClick={() => handleSort('accountName')}>
                    ACCOUNT NAME{' '}
                    {sortBy === 'accountName' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-150px' onClick={() => handleSort('reviewedBy')}>
                    REVIEWED BY{' '}
                    {sortBy === 'reviewedBy' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-150px' onClick={() => handleSort('decision')}>
                    REVIEW DECISION{' '}
                    {sortBy === 'decision' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('note')}>
                    NOTE{' '}
                    {sortBy === 'note' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-100px' onClick={() => handleSort('remediationStatus')}>
                    REMEDIATION STATUS{' '}
                    {sortBy === 'remediationStatus' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr style={{height: '100%'}}>
                    <td colSpan={6}>
                      <EmptyState
                        title='Loading Reviews'
                        description='Please wait while we fetch the review details'
                        isLoading={true}
                      />
                    </td>
                  </tr>
                ) : filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const system = item.system
                    const user = item.user
                    const note = user?.note || '--'
                    const isLongNote = note.length > 20

                    return (
                      <tr key={`${item.id}-${system.systemId}-${user.userId}`}>
                        <td style={{textAlign: 'left'}}>
                          <div className='d-flex align-items-center gap-2'>
                            {system?.icon && (
                              <img
                                src={toAbsoluteUrl(system.icon)}
                                alt={system.name}
                                className='me-2'
                                style={{width: '24px', height: '24px'}}
                              />
                            )}
                            <div>
                              <span className='fw-semibold d-block fs-6 text-dark'>
                                {system?.name || '--'}
                              </span>
                              <span className='text-gray-800 fs-7'>{system?.domain || '--'}</span>
                            </div>
                          </div>
                        </td>
                        <td style={{textAlign: 'left'}}>
                          <span className='text-dark fw-semibold d-block fs-6'>
                            {user?.name || '--'}
                          </span>
                        </td>
                        <td style={{textAlign: 'left'}}>
                          <span className='text-dark fw-semibold d-block fs-6'>
                            {user?.reviewBy ? <OwnerDisplay owner={user.reviewBy.name} /> : '--'}
                          </span>
                        </td>
                        <td style={{textAlign: 'left'}}>
                          <span className='d-flex align-items-center'>
                            {user?.status ? (
                              <StatusBadge
                                status={
                                  user?.status.charAt(0).toUpperCase() + user?.status.slice(1)
                                }
                              />
                            ) : (
                              '--'
                            )}
                          </span>
                        </td>
                        <td style={{textAlign: 'left'}}>
                          {isLongNote ? (
                            <OverlayTrigger
                              placement='right'
                              overlay={
                                <Tooltip
                                  id={`tooltip-note-${user.userId}`}
                                  className='text-justify'
                                >
                                  <span>{note}</span>
                                </Tooltip>
                              }
                            >
                              <span
                                className='text-dark fw-semibold d-block fs-6 note-text'
                                style={{
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                              >
                                {note}
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <span className='text-dark fw-semibold d-block fs-6'>{note}</span>
                          )}
                        </td>
                        <td style={{textAlign: 'left'}}>
                          {user?.remediatedNote || user?.remediatedFile ? (
                            <span
                              className='ki-duotone ki-check-circle text-success fs-2'
                              style={{marginLeft: 20}}
                            >
                              <span className='path1'></span>
                              <span className='path2'></span>
                            </span>
                          ) : (
                            <button
                              type='button'
                              className='btn btn-sm btn-light fs-6'
                              style={{
                                height: '40px',
                                background: '#fff',
                                color: '#000',
                                border: '1px solid #C5C5C5',
                              }}
                              onClick={() =>
                                handleUploadModal(
                                  item.id,
                                  system.systemId,
                                  user.userId,
                                  '',
                                  null,
                                  'Add'
                                )
                              }
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-download mb-1 me-2'
                                style={{marginRight: '4px'}}
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M12 5l0 14' />
                                <path d='M5 12l14 0' />
                              </svg>
                              Upload
                            </button>
                          )}
                        </td>
                        {(user?.remediatedNote || user?.remediatedFile) && (
                          <td>
                            <button
                              className='btn btn-sm btn-icon border bg-gray-100'
                              type='button'
                              onClick={() =>
                                handleUploadModal(
                                  item.id,
                                  system.systemId,
                                  user.userId,
                                  user.remediatedNote,
                                  user.remediatedFile,
                                  'Edit'
                                )
                              }
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-edit'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                <path d='M16 5l3 3' />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })
                ) : (
                  <tr style={{height: '100%'}}>
                    <td colSpan={6}>
                      <EmptyState
                        title='No Denied Access Reviews Found'
                        description='No accounts with denied access were found.'
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </KTCard>
      <UploadEvidenceModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        id={reviewId || ''}
        systemId={systemId}
        accountId={userId || ''}
        remediatedNote={remediatedNote}
        remediatedFile={remediatedFile}
        mode={mode}
      />
      <ToastContainer />
    </>
  )
}

export default AccessChange
